import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import _ from 'lodash';
import { getBricks } from '../../utils/api';
import { loadImage } from '../../utils/imageCache';

export interface Brick {
  type: string;
  image: string;
  displayImage: string;
}

export interface BrickSeries {
  _id: string;
  name: string;
  thumbnail: string;
  displayThumbnail: string | null;
  bricks: Brick[];
}

interface BricksState {
  loading: boolean;
  brickSeries: Record<string, BrickSeries>;
  failedImages: string[];
  error: string | null;
}

const initialState: BricksState = {
  loading: false,
  brickSeries: {},
  failedImages: [],
  error: null,
};

// Async thunk for fetching bricks
export const fetchBricks = createAsyncThunk(
  'bricks/fetchBricks',
  async () => {
    const { brickCollections } = await getBricks("5f95c5aa3755f3ed1d71b3d3");
    return _.keyBy(brickCollections, (b: BrickSeries) => b._id);
  }
);

// Async thunk for preparing series (loading images)
export const prepareSeries = createAsyncThunk(
  'bricks/prepareSeries',
  async (seriesId: string, { getState }) => {
    const state = getState() as { bricks: BricksState };
    const series = state.bricks.brickSeries[seriesId];
    
    const bricks = series?.bricks ?? [];
    const results = await Promise.allSettled(
      bricks.map((b: Brick) => loadImage(b.displayImage))
    );
    const failed = bricks
      .filter((_, i) => results[i].status === 'rejected')
      .map((b) => b.displayImage);

    return { seriesId, failed };
  }
);

const bricksSlice = createSlice({
  name: 'bricks',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBricks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBricks.fulfilled, (state, action) => {
        state.loading = false;
        state.brickSeries = action.payload;
      })
      .addCase(fetchBricks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch bricks';
      })
      .addCase(prepareSeries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(prepareSeries.fulfilled, (state, action) => {
        state.loading = false;
        const { failed } = action.payload;
        state.failedImages = [...new Set([...state.failedImages, ...failed])];
      })
      .addCase(prepareSeries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load brick images';
      });
  },
});

// Selectors
export const selectBrickSeries = (state: { bricks: BricksState }) => state.bricks.brickSeries;
export const selectSeriesList = createSelector(
  [selectBrickSeries],
  (brickSeries) => Object.values(brickSeries)
);
export const selectBricksLoading = (state: { bricks: BricksState }) => state.bricks.loading;
export const selectBricksError = (state: { bricks: BricksState }) => state.bricks.error;
export const selectFailedImages = (state: { bricks: BricksState }) => state.bricks.failedImages;

export const selectUsableBrickSeries = createSelector(
  [selectBrickSeries, selectFailedImages],
  (brickSeries, failedImages): Record<string, BrickSeries> => {
    if (failedImages.length === 0) return brickSeries;
    const failed = new Set(failedImages);
    return _.mapValues(brickSeries, (series) => ({
      ...series,
      bricks: series.bricks.filter((b) => !failed.has(b.displayImage)),
    }));
  }
);

export default bricksSlice.reducer;